import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Alert,
  Rating,
  Chip,
  Avatar,
  Card,
  CardContent,
} from '@mui/material';
import { X, Star, MessageSquare, User } from 'lucide-react';

interface ReviewFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  data?: {
    reviewId?: string;
    employeeName?: string;
    employeeAvatar?: string;
    reviewPeriod?: string;
    reviewType?: 'self' | 'peer' | 'manager';
  };
}

/**
 * ReviewFeedbackModal
 * Form for providing performance review feedback
 */
const ReviewFeedbackModal: React.FC<ReviewFeedbackModalProps> = ({ open, onClose, data }) => {
  const [overallRating, setOverallRating] = useState<number | null>(4);
  const [technicalRating, setTechnicalRating] = useState<number | null>(4);
  const [communicationRating, setCommunicationRating] = useState<number | null>(4);
  const [teamworkRating, setTeamworkRating] = useState<number | null>(4);
  const [strengths, setStrengths] = useState('');
  const [improvements, setImprovements] = useState('');
  const [goals, setGoals] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setSubmitted(true);
    // Reset and close after short delay
    setTimeout(() => {
      handleClose();
    }, 1500);
  };

  const handleClose = () => {
    setOverallRating(4);
    setTechnicalRating(4);
    setCommunicationRating(4);
    setTeamworkRating(4);
    setStrengths('');
    setImprovements('');
    setGoals('');
    setSubmitted(false);
    onClose();
  };

  const ratingLabels: Record<number, string> = {
    1: 'Needs Improvement',
    2: 'Below Expectations',
    3: 'Meets Expectations',
    4: 'Exceeds Expectations',
    5: 'Outstanding',
  };

  const RatingSection = ({
    label,
    value,
    onChange,
  }: {
    label: string;
    value: number | null;
    onChange: (value: number | null) => void;
  }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Rating
          value={value}
          onChange={(_, newValue) => onChange(newValue)}
          size="small"
        />
        {value && (
          <Chip
            label={ratingLabels[value]}
            size="small"
            color={value >= 4 ? 'success' : value >= 3 ? 'primary' : 'warning'}
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star size={20} />
            <Typography variant="h6" fontWeight={600}>
              Performance Review Feedback
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {submitted ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Review feedback submitted successfully! Thank you for your input.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Employee Info */}
            <Card variant="outlined">
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <Avatar src={data?.employeeAvatar} sx={{ width: 48, height: 48 }}>
                  <User size={24} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {data?.employeeName || 'Team Member'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Review Period: {data?.reviewPeriod || 'Q4 2024'} • 
                    Type: {data?.reviewType === 'self' ? 'Self Review' : 
                           data?.reviewType === 'peer' ? 'Peer Review' : 'Manager Review'}
                  </Typography>
                </Box>
                <Box sx={{ ml: 'auto' }}>
                  <Chip
                    label="In Progress"
                    color="primary"
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Ratings Section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Star size={16} />
                Performance Ratings
              </Typography>
              <Card variant="outlined">
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <RatingSection
                    label="Overall Performance"
                    value={overallRating}
                    onChange={setOverallRating}
                  />
                  <Divider />
                  <RatingSection
                    label="Technical Skills"
                    value={technicalRating}
                    onChange={setTechnicalRating}
                  />
                  <RatingSection
                    label="Communication"
                    value={communicationRating}
                    onChange={setCommunicationRating}
                  />
                  <RatingSection
                    label="Teamwork & Collaboration"
                    value={teamworkRating}
                    onChange={setTeamworkRating}
                  />
                </CardContent>
              </Card>
            </Box>

            {/* Written Feedback */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MessageSquare size={16} />
                Written Feedback
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Key Strengths"
                  value={strengths}
                  onChange={(e) => setStrengths(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="What does this person do well? What are their standout qualities?"
                />
                <TextField
                  label="Areas for Improvement"
                  value={improvements}
                  onChange={(e) => setImprovements(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="What could this person improve on? Be constructive and specific."
                />
                <TextField
                  label="Goals & Development Suggestions"
                  value={goals}
                  onChange={(e) => setGoals(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="What goals or development opportunities would you suggest?"
                />
              </Box>
            </Box>

            {/* Info */}
            <Alert severity="info">
              Your feedback is confidential and will be used to help support professional growth.
              Please be thoughtful and constructive.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          disabled={isSubmitting || submitted}
          sx={{ mr: 'auto' }}
        >
          Save Draft
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || submitted || !strengths || !improvements}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewFeedbackModal;
